import {Component, OnInit} from '@angular/core';
import {PublicService, Response, Role, User, UserLogin, WebUserService} from 'web-angular-client';
import {ActivatedRoute, Router} from '@angular/router';
import {SubmitForm} from '../../../sdk/interface/form/submitForm';
import {AbstractControl, UntypedFormBuilder, Validators} from '@angular/forms';
import {SyncValidators} from '../../../sdk/validator/SyncValidators';
import {AlertService} from '../../../sdk/service/alert.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {ConfirmationService} from '../../../sdk/service/confirmation.service';
import {EMPTY, Subject, timer} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {ROUTES} from '../../../app.routes';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {Location} from '@angular/common';
import ResponseEnum = Response.ResponseCodeEnum;

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: false
})
export class UserEditComponent extends SubmitForm implements OnInit
{
    selectedRole: Role;
    loading: boolean;
    user: User;
    private userId: number;
    private roleSubject: Subject<Role> = new Subject();

    constructor(
        protected activatedRoute: ActivatedRoute,
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected confirmationService: ConfirmationService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        protected userService: WebUserService,
        protected publicService: PublicService,
        protected ngxLoader: NgxUiLoaderService,
        private location: Location)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
    }

    ngOnInit()
    {
        this.form = this.formBuilder.group({
            enabled: [false],
            name: ['', Validators.required],
            surname: ['', Validators.required],
            email: [
                '',
                [Validators.required, Validators.email],
                this.validateEmailAddress.bind(this)
            ],
            mobileNumber: [
                '', [
                    Validators.required, SyncValidators.validateMobileNumber()
                ]
            ],
            agentId: [
                '',
                [SyncValidators.validateAgentId()]
            ]
        });

        this.loadFormElementsFromRouteParams();
    }

    back()
    {
        this.location.back();
    }

    getRoleSubject(): Subject<Role>
    {
        return this.roleSubject;
    }

    /**
     * Sets the role selected using the role-selector component
     */
    onRoleSelect(selectedRole: Role)
    {
        this.selectedRole = selectedRole;
    }

    getSelectedRole()
    {
        return this.selectedRole;
    }

    /**
     * Validate that an email address entered in the form input does not already exist
     * (excluding the users current email address).
     */
    validateEmailAddress(control: AbstractControl)
    {
        if (!control.value)
        {
            return EMPTY;
        }
        return timer(1000).pipe(
            switchMap(() =>
            {
                return this.publicService.validateUserExists(control.value).pipe(map((res) =>
                {
                    return (res.responseCode === ResponseEnum.TRUE && this.user.email != control.value)
                        ?
                        {duplicateEmail: true} : null;
                }));
            })
        );
    }

    submit()
    {
        if (!this.hasFormChanged())
        {
            this.loading = false;
            this.alertService.error('No changes have been made.');
            return;
        }

        this.confirmationService.confirm('Save Changes', 'Are you sure you wish to save these changes?')
            .then((confirmed) =>
            {
                this.loading = false;
                if (confirmed)
                {
                    let patchedUser = this.getUserPatchFromFormControls();
                    this.setUserRole(patchedUser);
                    this.ngxLoader.start();
                    this.userService.patchUser(this.userId, patchedUser).subscribe({
                        next: () =>
                        {
                            this.ngxLoader.stop();
                            this.alertService.success('User successfully updated', true);
                            this.router.navigate([ROUTES.SECURE.USER.LIST.path]).then();
                        },
                        error: (error) =>
                        {
                            this.ngxLoader.stop();
                            this.alertService.error(error);
                        }
                    });
                }
            })
            .catch(() =>
            {
                this.loading = false;
            });
    }

    /**
     * @return <i>true</i> if any form component has been marked as 'dirty'. Will capture any form
     * input subject to user input, regardless of whether the input value actually differs from the
     * original value.
     */
    public hasFormChanged(): boolean
    {
        return this.form.dirty ||
            (this.getSelectedRole() != null && this.getSelectedRole().name !== this.user.role.name);
    }

    /**
     * @return true if the user being edited is the user that is currently logged in. false
     * otherwise.
     */
    public isEditUserLoggedIn(): boolean
    {
        return this.userId == this.authenticationService.getUserLogin().userId;
    }

    /**
     * @return true if the currently logged-in user has the FIELD_AGENT role.
     */
    public isFieldAgent(): boolean
    {
        const userLogin: UserLogin = this.authenticationService.getUserLogin();
        return userLogin?.role?.name === Role.NameEnum.FIELD_AGENT;
    }

    /**
     * Loads all defined form controls from a {@link User} object matching the user ID passed
     * in as a parameter to the URI.
     */
    private loadFormElementsFromRouteParams()
    {
        this.ngxLoader.start();
        this.activatedRoute.params.subscribe(params =>
        {
            this.userId = params['id'];
            this.userService.getUserFromUserId(this.userId).subscribe({
                next: (user) =>
                {
                    this.user = user;
                    this.setFormControlsFromUser();
                    this.selectedRole = user.role;
                    this.roleSubject.next(this.user.role);
                    this.ngxLoader.stop();
                },
                error: error =>
                {
                    this.ngxLoader.stop();
                    this.debugOutput(error);
                    this.alertService.error(error);
                }
            });
        });
    }

    private setFormControlsFromUser()
    {
        this.form.controls.enabled.setValue(this.user.enabled);
        this.form.controls.name.setValue(this.user.name);
        this.form.controls.surname.setValue(this.user.surname);
        this.form.controls.email.setValue(this.user.email);
        this.form.controls.mobileNumber.setValue(this.user.mobileNumber);
        this.form.controls.agentId.setValue(this.user.agentId);

        if (this.isFieldAgent())
        {
            this.form.controls.agentId.disable();
        }
    }

    /**
     * Created a patched {@link User} object by setting fields that have been marked as
     * 'dirty' by form controls.
     */
    private getUserPatchFromFormControls(): User
    {
        let user: User = {};
        this.f.name.dirty && this.f.name.valid ? user.name = this.form.controls.name.value : null;
        this.f.surname.dirty && this.f.surname.valid ? user.surname = this.form.controls.surname.value : null;
        this.f.email.dirty && this.f.email.valid ? user.email = this.form.controls.email.value : null;
        this.f.mobileNumber.dirty && this.f.mobileNumber.valid ? user.mobileNumber = this.form.controls.mobileNumber.value : null;
        this.f.enabled.dirty ? user.enabled = this.f.enabled.value : null;
        this.f.agentId.dirty && this.f.agentId.valid ? user.agentId = this.form.controls.agentId.value?.trim() || null : null;
        this.debugOutput(JSON.stringify(user));
        return user;
    }

    /**
     * Set the role selected on a user to be patched/edited.
     */
    private setUserRole(user: User)
    {
        if (this.getSelectedRole().name !== this.user.role.name)
        {
            user.role = this.getSelectedRole();
        }
    }
}